import { confirmarResetDeSenhaSchema, senhaSchema } from '@locaveri/shared'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { AuthLayout } from '@/components/auth/auth-layout'
import { CampoDeSenha } from '@/components/auth/campo-de-senha'
import { MedidorDeForcaDeSenha } from '@/components/auth/medidor-de-forca-de-senha'
import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { redefinirSenha } from '@/features/auth/api'
import { aplicarErrosDaApi, aplicarErrosDoZod, validarCom } from '@/lib/formulario'
import { useParametroDaBusca } from '@/lib/rota'

/**
 * Definição da nova senha a partir do link recebido por e-mail.
 *
 * O token vem na URL (`/redefinir-senha?token=…`) e é de uso único: a API guarda
 * só o hash dele, e ele morre na primeira utilização. Esta tela nunca o guarda
 * em lugar nenhum — nem em storage, nem no store.
 *
 * A mesma tela atende o CONVITE de usuário interno, que também chega como um
 * link de definição de senha. Por isso a mensagem fala em "definir", e não em
 * "trocar".
 */

interface FormularioDeNovaSenha {
  senha: string
  confirmacaoDeSenha: string
}

const CAMPOS = ['senha', 'confirmacaoDeSenha'] as const

export function RedefinirSenhaPage(): ReactElement {
  const navigate = useNavigate()
  const token = useParametroDaBusca('token')
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormularioDeNovaSenha>({
    defaultValues: { senha: '', confirmacaoDeSenha: '' },
  })

  const senha = watch('senha')

  const redefinir = useMutation({
    mutationFn: redefinirSenha,
    onSuccess: () => {
      toast.success('Senha definida', {
        description: 'Entre com a nova senha para continuar.',
      })
      void navigate({ to: '/login' })
    },
    onError: (causa: unknown) => {
      setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
    },
  })

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    // Comparar com `null` é checar a AUSÊNCIA do parâmetro na URL, não comparar
    // segredo: não há valor secreto do outro lado, e portanto nada a vazar por
    // tempo de execução.
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (token === null) {
      setErroGeral('Link inválido: o token não veio no endereço.')
      return
    }

    const entrada = {
      token,
      senha: valores.senha,
      confirmacaoDeSenha: valores.confirmacaoDeSenha,
    }

    const validado = confirmarResetDeSenhaSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    redefinir.mutate(entrada)
  })

  const rodape = (
    <Link to="/login" className="text-brand text-sm underline-offset-4 hover:underline">
      Voltar para o login
    </Link>
  )

  // Ver acima: ausência do parâmetro, não comparação de segredo.
  // eslint-disable-next-line security/detect-possible-timing-attacks
  if (token === null) {
    return (
      <AuthLayout titulo="Link inválido" rodape={rodape}>
        <Alert variant="danger">
          <AlertTitle>Este endereço não tem um token de redefinição</AlertTitle>
          <AlertDescription>
            Abra o link exatamente como ele chegou no e-mail. Se ele já expirou, peça um novo.
          </AlertDescription>
        </Alert>

        <Button asChild variant="secondary" className="w-full">
          <Link to="/esqueci-senha">Pedir um novo link</Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      titulo="Definir nova senha"
      descricao="Escolha uma senha longa. Comprimento protege mais do que símbolo."
      rodape={rodape}
    >
      <form
        noValidate
        onSubmit={(evento) => {
          void aoEnviar(evento)
        }}
        className="space-y-4"
      >
        {erroGeral === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erroGeral}</AlertDescription>
          </Alert>
        )}

        <Campo
          rotulo="Nova senha"
          obrigatorio
          erro={errors.senha?.message}
          dica="Mínimo de 12 caracteres. Uma frase com quatro palavras funciona muito bem."
        >
          {(campo) => (
            <CampoDeSenha
              {...campo}
              {...register('senha', {
                required: 'informe a nova senha',
                validate: validarCom(senhaSchema),
              })}
              autoComplete="new-password"
              disabled={redefinir.isPending}
            />
          )}
        </Campo>

        <MedidorDeForcaDeSenha senha={senha} />

        <Campo rotulo="Confirme a nova senha" obrigatorio erro={errors.confirmacaoDeSenha?.message}>
          {(campo) => (
            <CampoDeSenha
              {...campo}
              {...register('confirmacaoDeSenha', { required: 'repita a nova senha' })}
              autoComplete="new-password"
              disabled={redefinir.isPending}
            />
          )}
        </Campo>

        <Button type="submit" className="w-full" disabled={redefinir.isPending}>
          {redefinir.isPending ? (
            <LoaderCircle className="animate-spin" aria-hidden="true" />
          ) : null}
          {redefinir.isPending ? 'Salvando…' : 'Definir senha'}
        </Button>
      </form>
    </AuthLayout>
  )
}
